import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Nav, Tab, Badge, Button, Alert, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import Layout from '../components/layouts/Main';
import OrdersOverview from '../components/dashboard/OrdersOverview';
import InventoryTracker from '../components/dashboard/InventoryTracker';
import QuickActions from '../components/dashboard/QuickActions';
import NotificationCenter from '../components/dashboard/NotificationCenter';
import OrdersTabbed from '../components/dashboard/OrdersTabbed';
import { dashboardApi, notificationGenerators } from '../utils/dashboardApi';
import '../components/dashboard/Dashboard.css';
  
const Dashboard = () => {
  const [dashboardData, setDashboardData] = useState({
    salesOrders: [],
    purchaseOrders: [],
    productionRequests: [],
    inventory: [],
    notifications: [],
    loading: true
  });

  const [activeTab, setActiveTab] = useState('overview');

  const fetchDashboardData = useCallback(async () => {
    try {
      setDashboardData(prev => ({ ...prev, loading: true }));

      const data = await dashboardApi.fetchDashboardData();
      const notifications = generateNotifications(data.salesOrders, data.purchaseOrders, data.productionRequests, data.inventory, data.pmInventory);

      console.log("dashboard inventory: " + JSON.stringify(data.inventory));
      setDashboardData({
        ...data,
        notifications,
        loading: false
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setDashboardData(prev => ({ ...prev, loading: false }));
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const generateNotifications = (salesOrders, purchaseOrders, productionRequests, inventory, pmInventory) => {
    const notifications = [
      ...notificationGenerators.generateLowStockNotifications(inventory),
      ...notificationGenerators.generateLowPackingMaterialStockNotifications(pmInventory),
      ...notificationGenerators.generateOverdueOrderNotifications(salesOrders)
    ];

    return notifications.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  };

  if (dashboardData.loading) {
    return (
      <Layout title="Master Dashboard">
        <Container fluid className="py-4">
          <div className="text-center">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
            <p className="mt-2">Loading dashboard...</p>
          </div>
        </Container>
      </Layout>
    );
  }

  // Generate stock alerts for display
  const generateStockAlerts = () => {
    const alerts = [];
    const lowStockProducts = dashboardData.inventory?.filter(item =>
      item.currentStock <= (item.minStock || 0)
    ) || [];

    const lowStockPM = dashboardData.pmInventory?.filter(item => {
      const availableStock = item.totalAvailableStock || 0;
      const reorderLevel = item.packingMaterial?.reorderQuantity || 0;
      return availableStock <= reorderLevel;
    }) || [];

          if (lowStockProducts.length > 0) {
            alerts.push(
              <Alert variant="warning" className="mb-3" key="product-alert">
                <i className="fas fa-exclamation-triangle me-2"></i>
                <strong>Low Stock Alert:</strong> {lowStockProducts.length} product(s) have low stock levels.
                <Link to="/operations-dashboard?tab=stock" className="alert-link ms-2">View Inventory</Link>
              </Alert>
            );
          }

          if (lowStockPM.length > 0) {
            alerts.push(
              <Alert variant="warning" className="mb-3" key="pm-alert">
                <i className="fas fa-exclamation-triangle me-2"></i>
                <strong>Packing Material Alert:</strong> {lowStockPM.length} packing material(s) need attention.
                <Link to="/operations-dashboard?tab=pmstock" className="alert-link ms-2">View Inventory</Link>
              </Alert>
            );
          }

    return alerts;
  };

  return (
    <Layout title="Master Dashboard">
      <Container fluid className="py-4">
        {/* Stock Alerts */}
        {!dashboardData.loading && generateStockAlerts()}

        <Row className="mb-4">
          <Col>
            <h2 className="mb-0">Master Dashboard</h2>
            <p className="text-muted">Complete overview of orders, inventory, and operations</p>
          </Col>
          <Col xs="auto">
            <Button variant="outline-primary" onClick={fetchDashboardData}>
              <i className="fas fa-sync-alt me-2"></i>Refresh
            </Button>
          </Col>
        </Row>

        <Tab.Container activeKey={activeTab} onSelect={setActiveTab}>
          <Nav variant="tabs" className="mb-4">
            <Nav.Item>
              <Nav.Link eventKey="overview">Overview</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="orders">Orders Tracking</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="inventory">Inventory</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="notifications">
                Notifications 
                {dashboardData.notifications.length > 0 && (
                  <Badge bg="danger" className="ms-1">
                    {dashboardData.notifications.length}
                  </Badge>
                )}
              </Nav.Link>
            </Nav.Item>
          </Nav>

          <Tab.Content>
            <Tab.Pane eventKey="overview">
              <Row className="g-4">
                <Col lg={8}>
                  <OrdersOverview data={dashboardData} />
                </Col>
                <Col lg={4}>
                  <QuickActions onRefresh={fetchDashboardData} />
                </Col>
              </Row>
            </Tab.Pane>

            <Tab.Pane eventKey="orders">
              <OrdersTabbed data={dashboardData} onRefresh={fetchDashboardData} />
            </Tab.Pane>

            <Tab.Pane eventKey="inventory">
              <InventoryTracker data={dashboardData.inventory} onRefresh={fetchDashboardData} />
            </Tab.Pane>

            <Tab.Pane eventKey="notifications">
              <NotificationCenter notifications={dashboardData.notifications} />
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>
      </Container>
    </Layout>
  );
};

export default Dashboard;
